import * as React from "react";
import { useLocation } from "wouter";
import { Target, RefreshCcw, Recycle, ArrowRight, ArrowLeft, MapPin } from "lucide-react";

/**
 * Placement category landing ("A Place for Everything") — the systematic /
 * structural organizing surface, i.e. the value-prop wedge. It groups the three
 * "get things to where they should be" sub-pages:
 *   - Give things a home (find-a-home · triage · unplaced/relocate) → /organize/find-homes
 *   - Rework a space (reorganize)                              → /organize/goals?new=1&type=reorganize
 *   - Declutter     (reduction — its own goal)                → /organize/declutter
 * This is one of the three top-level Organize categories: Placement · Events · Keep It Up.
 */
type CategoryCard = {
  key: string;
  label: string;
  hint: string;
  icon: React.ElementType;
  iconClass: string;
  assistive?: boolean;
  href: string;
};

const SUBPAGES: CategoryCard[] = [
  { key: "find-homes", label: "Give things a home", hint: "Figure out where things should live — by scan, by item, or from your no-home list", icon: Target, iconClass: "text-primary", href: "/organize/find-homes" },
  { key: "reorganize", label: "Rework a space", hint: "Rework how a room or container is organized", icon: RefreshCcw, iconClass: "text-primary", href: "/organize/goals?new=1&type=reorganize" },
  { key: "declutter", label: "Declutter", hint: "Review unused things and let them go", icon: Recycle, iconClass: "text-sage-deep", assistive: true, href: "/organize/declutter" },
];

export default function PlacementPage() {
  const [, navigate] = useLocation();

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <header>
        <button
          onClick={() => navigate("/organize")}
          className="inline-flex items-center gap-1 text-sm text-faint hover:text-primary transition-colors mb-2"
        >
          <ArrowLeft className="w-4 h-4" />Organize
        </button>
        <div className="flex items-center gap-2 mb-1">
          <MapPin className="w-5 h-5 text-primary" />
          <span className="text-sm font-medium text-primary">Placement</span>
        </div>
        <h1 className="text-4xl font-semibold text-ink font-headline tracking-[-0.02em]">A place for everything</h1>
        <p className="text-body mt-1 text-sm">Get your things to where they should be — and keep the safety net to change your mind.</p>
      </header>

      <div className="space-y-3">
        {SUBPAGES.map((c) => {
          const Icon = c.icon;
          return (
            <a
              key={c.key}
              href={c.href}
              className="flex items-center gap-3 bg-white rounded-lg border border-clay-border p-4 hover:border-primary/40 transition-colors group"
            >
              <div className={`w-10 h-10 rounded-md flex items-center justify-center shrink-0 ${c.assistive ? "bg-sage-wash" : "bg-amethyst-soft"}`}>
                <Icon className={`w-5 h-5 ${c.iconClass}`} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[1.0625rem] font-semibold text-ink">{c.label}</div>
                <div className="text-[0.8125rem] text-faint">{c.hint}</div>
              </div>
              <ArrowRight className="w-4 h-4 text-faint group-hover:text-primary transition-colors shrink-0" />
            </a>
          );
        })}
      </div>
    </div>
  );
}
